import React from 'react';

/**
 * Confirm your session details — resend-confirmation popup (Figma node 7600-52100).
 *
 * Read-only review of a session request's details, shown when care clicks "Resend" on the
 * dashboard. Edit closes the popup; Confirm Request re-broadcasts to matched teachers.
 */

const one_line = (v) => (v ? String(v).replace(/\n/g, ' - ') : '-');

const section_title = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--text-primary)'
};

function DetailRow({ label, value }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 24
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{label}</span>
      <span
        style={{
          fontSize: 15,
          fontWeight: 500,
          color: 'var(--text-label)',
          textAlign: 'right'
        }}
      >
        {value}
      </span>
    </div>
  );
}

function GoalItem({ index, label }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          flexShrink: 0,
          borderRadius: 13,
          background: 'var(--surface-card)',
          border: '1px solid var(--border-default)',
          color: 'var(--text-muted)',
          fontSize: 12,
          fontWeight: 600
        }}
      >
        {index}
      </span>
      <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-label)' }}>{label}</span>
    </div>
  );
}

function ConfirmSessionDetails({ row = {}, confirming = false, onEdit, onConfirm }) {
  const duration = row.duration || '-';
  const language = row.language || '-';
  const gender = row.gender || '-';
  const goals = row.goals && row.goals.length ? row.goals : [];
  const comment = row.comment || '-';

  return (
    <div style={{ width: 521, maxWidth: '90vw' }}>
      <p style={{ margin: 0, fontSize: 19, fontWeight: 600, color: 'var(--text-primary)' }}>
        Confirm your session details
      </p>
      <p
        style={{
          margin: '6px 0 26px',
          fontSize: 14.5,
          fontWeight: 500,
          color: 'var(--text-muted)'
        }}
      >
        Review session details below
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={section_title}>Basic Details</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <DetailRow label="Student Date & Time (PDT)" value={one_line(row.student_time)} />
            <DetailRow label="Teacher Date & Time (IST)" value={one_line(row.teacher_time)} />
            <DetailRow label="Duration" value={duration} />
            <DetailRow label="Language spoken" value={language} />
            <DetailRow label="Gender of coach" value={gender} />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={section_title}>Goals teacher should know</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 15 }}>
            {goals.length ? (
              goals.map((g, i) => <GoalItem key={g} index={i + 1} label={g} />)
            ) : (
              <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>—</span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          <p style={section_title}>Comment for this session</p>
          <div
            style={{
              minHeight: 77,
              padding: 15,
              borderRadius: 10,
              background: 'var(--input-bg)',
              border: '1px solid var(--border-default)',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--text-muted)'
            }}
          >
            {comment}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" style={editBtn} onClick={onEdit} disabled={confirming}>
            Edit
          </button>
          <button
            type="button"
            style={{
              ...confirmBtn,
              opacity: confirming ? 0.6 : 1,
              cursor: confirming ? 'wait' : 'pointer'
            }}
            onClick={onConfirm}
            disabled={confirming}
          >
            {confirming ? 'Sending…' : 'Confirm Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

const editBtn = {
  width: 159,
  padding: '14px 22px',
  borderRadius: 14,
  border: '1px solid var(--border-default)',
  background: 'var(--surface-card)',
  color: 'var(--input-text)',
  fontSize: 17,
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0px 2px 4px 0px rgba(188, 188, 188, 0.22)'
};

const confirmBtn = {
  padding: '14px 22px',
  borderRadius: 14,
  border: 'none',
  background: 'var(--brand-primary)',
  color: '#ffffff',
  fontSize: 17,
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0px 2px 4px 0px rgba(248, 102, 36, 0.15)'
};

export default ConfirmSessionDetails;
